// src/contexts/TenantContext.tsx
// Context para manejar el estado del tenant globalmente

import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import type { ReactNode } from 'react';
import { config } from '../config/env';
import { apiService } from '../services/api';
import { authStorage } from '../lib/storage';
import type { TenantData } from '../types';
import type { ConversationCard, Layer } from '../types/dashboard';

interface TenantContextType {
  tenant: TenantData | null;
  loading: boolean;
  error: string | null;
  conversationCards: ConversationCard | null;
  customFieldsCards: Layer[] | null;
  loadingInfoWindow: boolean;
  isOpenInfoWindow: boolean;
  setConversationCards: (conversationCards: ConversationCard) => void;
  setCustomFieldsCards: (customFieldsCards: Layer[]) => void;
  setLoadingInfoWindow: (loadingInfoWindow: boolean) => void;
  setIsOpenInfoWindow: (isOpenInfoWindow: boolean) => void;
  fetchTenant: (tenancyName?: string) => Promise<void>;
  clearTenant: () => void;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

// Hook personalizado para usar el contexto
export const useTenant = () =>
{
  const context = useContext(TenantContext);
  if (!context)
  {
    throw new Error('useTenant debe ser usado dentro de TenantProvider');
  }
  return context;
};

interface TenantProviderProps {
  children: ReactNode;
}

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) =>
{
  const [tenant, setTenant] = useState<TenantData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [conversationCards, setConversationCards] = useState<ConversationCard | null>(null);
  const [customFieldsCards, setCustomFieldsCards] = useState<Layer[] | null>([]);
  const [loadingInfoWindow, setLoadingInfoWindow] = useState<boolean>(false);
  const [isOpenInfoWindow, setIsOpenInfoWindow] = useState<boolean>(false);

  // Ref para rastrear si ya se inicializó el tenant
  const initializedRef = useRef(false);
  const fetchingRef = useRef(false);

  const fetchTenant = useCallback(async (tenancyName?: string) =>
  {
    // Prevenir múltiples fetches simultáneos
    if (fetchingRef.current)
    {
      return;
    }

    fetchingRef.current = true;
    setLoading(true);
    setError(null);

    try
    {
      const data = await apiService.getTenant(tenancyName);

      // Guardar en localStorage para persistencia usando authStorage
      authStorage.setTenantData(data);
      setTenant(data);
    }
    catch (err)
    {
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido';
      setError(errorMessage);
      console.error('❌ TenantContext: Error fetching tenant:', err);
    }
    finally
    {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, []);

  const clearTenant = useCallback(() =>
  {
    setTenant(null);
    authStorage.removeTenantData();
    initializedRef.current = false;
  }, []);

  // Cargar tenant desde localStorage o hacer fetch al iniciar - SOLO UNA VEZ
  useEffect(() =>
  {
    // Si ya se inicializó, no hacer nada
    if (initializedRef.current)
    {
      return;
    }

    const loadTenant = async () =>
    {
      // Intentar cargar desde localStorage primero
      const savedTenant = authStorage.getTenantData();

      if (savedTenant)
      {
        setTenant(savedTenant);
      }
      else
      {
        // Solo hacer fetch si NO hay tenant guardado
        await fetchTenant(config.tenant.defaultName);
      }

      // Marcar como inicializado
      initializedRef.current = true;
    };

    loadTenant();
  }, [fetchTenant]); // fetchTenant está memoizado con useCallback

  const value: TenantContextType = {
    tenant,
    loading,
    error,
    conversationCards,
    customFieldsCards,
    loadingInfoWindow,
    isOpenInfoWindow,
    setConversationCards,
    setCustomFieldsCards,
    setLoadingInfoWindow,
    setIsOpenInfoWindow,
    fetchTenant,
    clearTenant,
  };

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
};
